// HEAVY module — imports raw recharts. Never import this file directly from a
// page; go through `charts/lazy.tsx` so the library stays in its own chunk.
import * as React from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { type ChartPalette, useChartPalette } from "./chart-theme";
import type { TrendChartPoint, TrendChartProps } from "./chart-types";

interface TrendTooltipPayloadEntry {
  payload?: TrendChartPoint;
}

interface TrendTooltipProps {
  active?: boolean;
  payload?: TrendTooltipPayloadEntry[];
  formatValue: (value: number) => string;
  valueLabel: string;
}

// HTML tooltip — CSS var() works here (only SVG attributes need resolution).
function TrendTooltip({ active, payload, formatValue, valueLabel }: TrendTooltipProps): React.ReactElement | null {
  const point: TrendChartPoint | undefined = active ? payload?.[0]?.payload : undefined;
  if (!point) return null;
  return (
    <div className="aichat-analytics__chart-tooltip">
      <div className="aichat-analytics__chart-tooltip-title">{point.label}</div>
      <div className="aichat-analytics__chart-tooltip-row">
        {formatValue(point.value)} {valueLabel}
      </div>
      <div className="aichat-analytics__chart-tooltip-row aichat-analytics__chart-tooltip-row--muted">
        {point.requestCount.toLocaleString("en-US")} chats
      </div>
    </div>
  );
}

function TrendChart({ points, height = 220, formatValue, valueLabel }: TrendChartProps): React.ReactElement {
  const palette: ChartPalette = useChartPalette();
  // Thin the x-axis ticks so long ranges (90 daily points) stay readable.
  let tickInterval = 0;
  if (points.length > 45) tickInterval = 13;
  else if (points.length > 14) tickInterval = 6;

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="aichatTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={palette.accent} stopOpacity={0.28} />
              <stop offset="100%" stopColor={palette.accent} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={palette.grid} strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: palette.axisText, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            interval={tickInterval}
            minTickGap={16}
          />
          <YAxis
            tick={{ fill: palette.axisText, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={52}
            tickFormatter={(value: number): string => formatValue(value)}
            allowDecimals={false}
          />
          <Tooltip
            content={<TrendTooltip formatValue={formatValue} valueLabel={valueLabel} />}
            cursor={{ stroke: palette.grid }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={palette.accent}
            strokeWidth={2}
            fill="url(#aichatTrendFill)"
            dot={false}
            activeDot={{ r: 4, fill: palette.accent, strokeWidth: 0 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default TrendChart;
